// SMSF Investment Strategy Builder
// Compliant with ATO Regulation 4.09 SIS Act 1993
// Generates audit-ready investment strategy documents

import React, { useState, useEffect } from 'react';
import { SimulationEngine } from '../SimulationEngine';
import { FinancialConfig } from '../FinancialConfig';
import { 
    generateInvestmentStrategyPDF, 
    generateTrusteeMinutesPDF, 
    generateReviewChecklistPDF 
} from '../utils/InvestmentStrategyPDFGenerator';

export const InvestmentStrategyBuilder = ({ smsfState, onSave, onCancel }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [strategyData, setStrategyData] = useState({
        // Step 1: Member Details
        members: [
            {
                name: '',
                age: '',
                balance: '',
                employmentStatus: 'retired',
                retirementAge: '',
                riskTolerance: 'balanced',
                objectives: '',
                financialCommitments: '',
                dependents: 0,
                externalSuper: ''
            }
        ],
        fundDetails: {
            fundName: smsfState?.fundName || '',
            abn: smsfState?.abn || '',
            establishedDate: smsfState?.establishedDate || ''
        },
        
        // Step 2: Investment Objectives
        objectives: {
            targetReturn: 'CPI + 3%',
            timeHorizon: '10',
            maxDrawdown: '20',
            capitalPreservation: true,
            incomeGeneration: false,
            targetIncome: ''
        },
        
        // Step 3: Asset Allocation
        assetAllocation: {
            ausShares: { current: 0, targetMin: 0, targetMax: 0, holdings: [] },
            intlShares: { current: 0, targetMin: 0, targetMax: 0, holdings: [] },
            property: { current: 0, targetMin: 0, targetMax: 0, holdings: [] },
            bonds: { current: 0, targetMin: 0, targetMax: 0, holdings: [] },
            cash: { current: 0, targetMin: 0, targetMax: 0, holdings: [] }
        },
        rebalancing: {
            trigger: '5',
            frequency: 'quarterly',
            method: 'tax-efficient'
        },
        
        // Step 4: Liquidity Assessment
        liquidity: {
            pensionPayments: [],
            operatingExpenses: {
                adminFees: 3500,
                auditFees: 1500,
                accountingFees: 2000,
                insurancePremiums: 0,
                other: 0
            },
            contingencyReserve: '',
            minimumCash: '',
            liquiditySources: []
        },
        
        // Step 5: Insurance
        insurance: {
            members: []
        },
        
        // Step 6: Review Schedule
        review: {
            nextReviewDate: '',
            reviewTriggers: []
        },
        
        // Compliance tracking
        compliance: {
            allElementsAddressed: false,
            specificToFund: false,
            matchesActualInvestments: false,
            dateRecorded: false,
            reviewScheduled: false,
            trusteeResolution: false
        }
    });
    
    const [validationErrors, setValidationErrors] = useState([]);
    const [complianceScore, setComplianceScore] = useState(0);
    
    // Auto-populate from existing SMSF calculator data
    useEffect(() => {
        if (smsfState) {
            setStrategyData(prev => ({
                ...prev,
                fundDetails: {
                    fundName: smsfState.fundName || '',
                    abn: smsfState.abn || '',
                    establishedDate: smsfState.establishedDate || ''
                },
                members: smsfState.members?.map(m => ({
                    name: m.name || '',
                    age: m.age || '',
                    balance: m.balance || '',
                    employmentStatus: m.age >= 67 ? 'retired' : 'employed',
                    retirementAge: m.retirementAge || 67,
                    riskTolerance: 'balanced',
                    objectives: '',
                    financialCommitments: '',
                    dependents: 0,
                    externalSuper: ''
                })) || prev.members
            }));
        }
    }, [smsfState]);
    
    // Auto-populate asset allocation current % from Asset Valuation Manager
    useEffect(() => {
        if (smsfState && smsfState.assetValues) {
            const assetVals = smsfState.assetValues;
            
            // Calculate total portfolio value
            const totalValue = (assetVals.australian_shares || 0) +
                             (assetVals.international_shares_ex_us || 0) +
                             (assetVals.us_shares || 0) +
                             (assetVals.property || 0) +
                             (assetVals.fixed_income || 0) +
                             (assetVals.cash || 0) +
                             (assetVals.alternatives || 0);
            
            if (totalValue > 0) {
                // Calculate percentages
                const ausSharesPct = ((assetVals.australian_shares || 0) / totalValue * 100).toFixed(1);
                const intlSharesPct = (((assetVals.international_shares_ex_us || 0) + (assetVals.us_shares || 0)) / totalValue * 100).toFixed(1);
                const propertyPct = ((assetVals.property || 0) / totalValue * 100).toFixed(1);
                const bondsPct = ((assetVals.fixed_income || 0) / totalValue * 100).toFixed(1);
                const cashPct = ((assetVals.cash || 0) / totalValue * 100).toFixed(1);
                
                setStrategyData(prev => ({
                    ...prev,
                    assetAllocation: {
                        ...prev.assetAllocation,
                        ausShares: { ...prev.assetAllocation.ausShares, current: ausSharesPct },
                        intlShares: { ...prev.assetAllocation.intlShares, current: intlSharesPct },
                        property: { ...prev.assetAllocation.property, current: propertyPct },
                        bonds: { ...prev.assetAllocation.bonds, current: bondsPct },
                        cash: { ...prev.assetAllocation.cash, current: cashPct }
                    }
                }));
                
                console.log('Investment Strategy Builder - Auto-populated from Asset Valuation Manager:', {
                    totalValue,
                    percentages: { ausSharesPct, intlSharesPct, propertyPct, bondsPct, cashPct }
                });
            }
        }
    }, [smsfState]);
    
    // Calculate compliance score
    useEffect(() => {
        const checks = [];
        
        // 1. Member circumstances documented
        const hasMembers = strategyData.members.length > 0 && 
            strategyData.members.every(m => m.name && m.age && m.balance);
        checks.push(hasMembers);
        
        // 2. Investment objectives specific
        const hasObjectives = strategyData.objectives.targetReturn && 
            strategyData.objectives.timeHorizon;
        checks.push(hasObjectives);
        
        // 3. Asset allocation defined
        const totalAllocation = Object.values(strategyData.assetAllocation)
            .reduce((sum, asset) => sum + (Number(asset.targetMax) || 0), 0);
        const hasAllocation = totalAllocation > 0;
        checks.push(hasAllocation);
        
        // 4. Liquidity assessed
        const hasLiquidity = strategyData.liquidity.minimumCash || 
            strategyData.liquidity.pensionPayments.length > 0;
        checks.push(hasLiquidity);
        
        // 5. Insurance considered
        const hasInsurance = strategyData.insurance.members.length > 0;
        checks.push(hasInsurance);
        
        // 6. Review scheduled
        const hasReview = strategyData.review.nextReviewDate;
        checks.push(hasReview);
        
        const score = (checks.filter(Boolean).length / 6) * 100;
        setComplianceScore(Math.round(score));
    }, [strategyData]);
    
    const wizardSteps = [
        {
            id: 'intro',
            title: 'Investment Strategy Requirements',
            description: 'Understanding your legal obligations'
        },
        {
            id: 'members',
            title: 'Member Details',
            description: 'Document member circumstances (Mandatory Element #6)'
        },
        {
            id: 'objectives',
            title: 'Investment Objectives',
            description: 'Risk and return targets (Mandatory Element #1)'
        },
        {
            id: 'allocation',
            title: 'Asset Allocation',
            description: 'Diversification strategy (Mandatory Element #2)'
        },
        {
            id: 'liquidity',
            title: 'Liquidity Assessment',
            description: 'Cash flow and reserves (Mandatory Elements #3 & #4)'
        },
        {
            id: 'insurance',
            title: 'Insurance Consideration',
            description: 'Life, TPD, income protection (Mandatory Element #5)'
        },
        {
            id: 'review',
            title: 'Review Schedule',
            description: 'Annual review and triggers'
        },
        {
            id: 'summary',
            title: 'Review & Generate',
            description: 'Compliance check and PDF export'
        }
    ];
    
    const handleNext = () => {
        // Validate current step
        const errors = validateStep(currentStep);
        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }
        
        setValidationErrors([]);
        if (currentStep < wizardSteps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };
    
    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            setValidationErrors([]);
        }
    };
    
    const validateStep = (stepIndex) => {
        const errors = [];
        
        switch(stepIndex) {
            case 1: // Members
                if (strategyData.members.length === 0) {
                    errors.push('At least one member required');
                }
                strategyData.members.forEach((m, idx) => {
                    if (!m.name) errors.push(`Member ${idx + 1}: Name required`);
                    if (!m.age || m.age < 18 || m.age > 100) errors.push(`Member ${idx + 1}: Valid age required`);
                    if (!m.balance || m.balance < 0) errors.push(`Member ${idx + 1}: Balance required`);
                });
                break;
                
            case 2: // Objectives
                if (!strategyData.objectives.targetReturn) {
                    errors.push('Target return required (e.g., "CPI + 3%")');
                }
                if (!strategyData.objectives.timeHorizon || strategyData.objectives.timeHorizon < 1) {
                    errors.push('Time horizon required (years)');
                }
                break;
                
            case 3: // Asset Allocation
                const total = Object.values(strategyData.assetAllocation)
                    .reduce((sum, asset) => sum + (Number(asset.targetMax) || 0), 0);
                if (total === 0) {
                    errors.push('Asset allocation required (at least one asset class)');
                }
                break;
                
            case 4: // Liquidity
                if (!strategyData.liquidity.minimumCash || strategyData.liquidity.minimumCash < 0) {
                    errors.push('Minimum cash reserve required');
                }
                break;
                
            case 5: // Insurance
                if (strategyData.insurance.members.length === 0) {
                    errors.push('Insurance assessment required for all members (can be "Not required")');
                }
                break;
                
            case 6: // Review
                if (!strategyData.review.nextReviewDate) {
                    errors.push('Next review date required');
                }
                break;
        }
        
        return errors;
    };
    
    const addMember = () => {
        setStrategyData(prev => ({
            ...prev,
            members: [...prev.members, {
                name: '',
                age: '',
                balance: '',
                employmentStatus: 'retired',
                retirementAge: '',
                riskTolerance: 'balanced',
                objectives: '',
                financialCommitments: '',
                dependents: 0,
                externalSuper: ''
            }]
        }));
    };
    
    const removeMember = (index) => {
        setStrategyData(prev => ({
            ...prev,
            members: prev.members.filter((_, idx) => idx !== index)
        }));
    };
    
    const updateMember = (index, field, value) => {
        setStrategyData(prev => ({
            ...prev,
            members: prev.members.map((m, idx) => 
                idx === index ? { ...m, [field]: value } : m
            )
        }));
    };
    
    const generatePDF = () => {
        try {
            // Generate all 3 documents
            const strategyPDF = generateInvestmentStrategyPDF(strategyData);
            const minutesPDF = generateTrusteeMinutesPDF(strategyData);
            const checklistPDF = generateReviewChecklistPDF(strategyData);
            
            // Download Investment Strategy
            strategyPDF.save(`SMSF_Investment_Strategy_${strategyData.fundDetails.fundName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
            
            // Prompt for additional documents
            setTimeout(() => {
                if (window.confirm('Download Trustee Minutes template?')) {
                    minutesPDF.save(`SMSF_Trustee_Minutes_${new Date().toISOString().split('T')[0]}.pdf`);
                }
            }, 500);
            
            setTimeout(() => {
                if (window.confirm('Download Review Checklist?')) {
                    checklistPDF.save(`SMSF_Review_Checklist_${new Date().toISOString().split('T')[0]}.pdf`);
                }
            }, 1000);
            
            // Show success message
            setTimeout(() => {
                alert('✅ Investment Strategy documents generated successfully!\n\n' +
                      '📄 Documents created:\n' +
                      '1. Investment Strategy (main document)\n' +
                      '2. Trustee Minutes template (for adoption)\n' +
                      '3. Annual Review Checklist\n\n' +
                      '⚖️ These documents are ATO-compliant and audit-ready.');
                
                if (onSave) onSave(strategyData);
            }, 1500);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please check all required fields are completed.');
        }
    };
    
    const renderStepContent = () => {
        switch(currentStep) {
            case 0:
                return renderIntroStep();
            case 1:
                return renderMemberStep();
            case 2:
                return renderObjectivesStep();
            case 3:
                return renderAllocationStep();
            case 4:
                return renderLiquidityStep();
            case 5:
                return renderInsuranceStep();
            case 6:
                return renderReviewStep();
            case 7:
                return renderSummaryStep();
            default:
                return null;
        }
    };
    
    const renderIntroStep = () => (
        <div className="space-y-6">
            <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-600">
                <h3 className="font-bold text-red-800 text-xl mb-3">⚖️ Legal Requirement</h3>
                <p className="text-red-700 mb-3">
                    <strong>Regulation 4.09 of the SIS Act 1993</strong> requires every SMSF to have a written investment strategy.
                </p>
                <div className="bg-red-100 p-4 rounded">
                    <p className="text-red-800 text-sm mb-2"><strong>Failure to comply can result in:</strong></p>
                    <ul className="list-disc list-inside text-red-700 text-sm space-y-1 ml-4">
                        <li>ATO penalties</li>
                        <li>Non-complying fund status</li>
                        <li>Loss of tax concessions (15% → 45%)</li>
                        <li>Failed audit</li>
                    </ul>
                </div>
            </div>
            
            <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="font-bold text-green-800 text-xl mb-3">✅ This Tool Will Help You:</h3>
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-100 p-4 rounded">
                        <strong className="text-green-900">1. Address All 6 Mandatory Elements</strong>
                        <p className="text-xs text-green-700 mt-1">Risk/return, diversification, liquidity, benefits, insurance, member circumstances</p>
                    </div>
                    <div className="bg-green-100 p-4 rounded">
                        <strong className="text-green-900">2. Generate Compliant Document</strong>
                        <p className="text-xs text-green-700 mt-1">Audit-ready PDF with all required details</p>
                    </div>
                    <div className="bg-green-100 p-4 rounded">
                        <strong className="text-green-900">3. Auto-Populate Data</strong>
                        <p className="text-xs text-green-700 mt-1">Imports from your SMSF calculator inputs</p>
                    </div>
                    <div className="bg-green-100 p-4 rounded">
                        <strong className="text-green-900">4. Check Compliance</strong>
                        <p className="text-xs text-green-700 mt-1">Real-time validation against ATO requirements</p>
                    </div>
                </div>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="font-bold text-blue-800 text-xl mb-3">📋 How It Works</h3>
                <ol className="list-decimal list-inside text-blue-700 space-y-2">
                    <li><strong>Member Details:</strong> Document each member's circumstances</li>
                    <li><strong>Investment Objectives:</strong> Set specific, measurable targets</li>
                    <li><strong>Asset Allocation:</strong> Define target ranges for each asset class</li>
                    <li><strong>Liquidity:</strong> Calculate cash flow needs and reserves</li>
                    <li><strong>Insurance:</strong> Assess life/TPD requirements</li>
                    <li><strong>Review Schedule:</strong> Set annual review date</li>
                    <li><strong>Generate PDF:</strong> Download compliant strategy document</li>
                </ol>
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-yellow-800 text-sm">
                    <strong>⏱️ Time required:</strong> Approximately 15-20 minutes to complete all steps
                </p>
            </div>
        </div>
    );
    
    const renderMemberStep = () => (
        <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-2">👥 Member Circumstances (Mandatory Element #6)</h3>
                <p className="text-sm text-blue-700">
                    Document each SMSF member's age, employment status, balance, and investment objectives.
                    This information is required by the ATO and will be reviewed during your annual audit.
                </p>
            </div>
            
            {strategyData.members.map((member, index) => (
                <div key={index} className="bg-white p-6 rounded-lg border-2 border-gray-200">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-gray-800">Member {index + 1}</h4>
                        {strategyData.members.length > 1 && (
                            <button
                                onClick={() => removeMember(index)}
                                className="text-red-600 hover:text-red-800 text-sm"
                            >
                                ✕ Remove
                            </button>
                        )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Full Name *
                            </label>
                            <input
                                type="text"
                                value={member.name}
                                onChange={(e) => updateMember(index, 'name', e.target.value)}
                                placeholder="John Smith"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Age *
                            </label>
                            <input
                                type="number"
                                value={member.age}
                                onChange={(e) => updateMember(index, 'age', e.target.value)}
                                placeholder="62"
                                min="18"
                                max="100"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Super Balance *
                            </label>
                            <input
                                type="number"
                                value={member.balance}
                                onChange={(e) => updateMember(index, 'balance', e.target.value)}
                                placeholder="800000"
                                min="0"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Employment Status *
                            </label>
                            <select
                                value={member.employmentStatus}
                                onChange={(e) => updateMember(index, 'employmentStatus', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="retired">Fully retired</option>
                                <option value="semi-retired">Semi-retired / part-time</option>
                                <option value="employed">Full-time employed</option>
                            </select>
                        </div>
                        
                        {member.employmentStatus !== 'retired' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Planned Retirement Age
                                </label>
                                <input
                                    type="number"
                                    value={member.retirementAge}
                                    onChange={(e) => updateMember(index, 'retirementAge', e.target.value)}
                                    placeholder="67"
                                    min={member.age}
                                    max="75"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        )}
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Risk Tolerance *
                            </label>
                            <select
                                value={member.riskTolerance}
                                onChange={(e) => updateMember(index, 'riskTolerance', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="conservative">Conservative (20% growth / 80% defensive)</option>
                                <option value="balanced">Balanced (50% growth / 50% defensive)</option>
                                <option value="growth">Growth (70% growth / 30% defensive)</option>
                                <option value="high-growth">High Growth (85% growth / 15% defensive)</option>
                            </select>
                        </div>
                        
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Investment Objectives
                            </label>
                            <textarea
                                value={member.objectives}
                                onChange={(e) => updateMember(index, 'objectives', e.target.value)}
                                placeholder="E.g., 'Capital preservation for retirement income of $50,000/year'"
                                rows="2"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Number of Dependents
                            </label>
                            <input
                                type="number"
                                value={member.dependents}
                                onChange={(e) => updateMember(index, 'dependents', e.target.value)}
                                placeholder="0"
                                min="0"
                                max="10"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Other Super (outside SMSF)
                            </label>
                            <input
                                type="number"
                                value={member.externalSuper}
                                onChange={(e) => updateMember(index, 'externalSuper', e.target.value)}
                                placeholder="300000"
                                min="0"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Affects diversification assessment</p>
                        </div>
                    </div>
                </div>
            ))}
            
            {strategyData.members.length < 6 && (
                <button
                    onClick={addMember}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition"
                >
                    + Add Another Member
                </button>
            )}
            
            <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-sm text-yellow-800">
                    <strong>💡 Why this matters:</strong> The ATO requires your strategy to consider each member's specific circumstances.
                    Generic statements like "Member 1" will fail audit. Use actual names and specific details.
                </p>
            </div>
        </div>
    );
    
    const renderObjectivesStep = () => (
        <div className="space-y-6">
            <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-2">🎯 Investment Objectives (Mandatory Element #1)</h3>
                <p className="text-sm text-green-700">
                    Your objectives must be <strong>specific and measurable</strong>. Vague statements like "maximize returns" will not pass audit.
                </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-4">Primary Investment Objective</h4>
                
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Target Return *
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            {['CPI + 2%', 'CPI + 3%', 'CPI + 4%', 'CPI + 5%'].map(option => (
                                <button
                                    key={option}
                                    onClick={() => setStrategyData(prev => ({
                                        ...prev,
                                        objectives: { ...prev.objectives, targetReturn: option }
                                    }))}
                                    className={`py-3 px-4 rounded-lg border-2 transition ${
                                        strategyData.objectives.targetReturn === option
                                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                                            : 'border-gray-300 hover:border-blue-300'
                                    }`}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                        <input
                            type="text"
                            value={strategyData.objectives.targetReturn}
                            onChange={(e) => setStrategyData(prev => ({
                                ...prev,
                                objectives: { ...prev.objectives, targetReturn: e.target.value }
                            }))}
                            placeholder="Or enter custom (e.g., '7% p.a. nominal')"
                            className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Example: "CPI + 3%" means 3% above inflation</p>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Time Horizon (years) *
                        </label>
                        <input
                            type="number"
                            value={strategyData.objectives.timeHorizon}
                            onChange={(e) => setStrategyData(prev => ({
                                ...prev,
                                objectives: { ...prev.objectives, timeHorizon: e.target.value }
                            }))}
                            placeholder="10"
                            min="1"
                            max="40"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Typically 10 years for balanced strategies</p>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Maximum Acceptable Drawdown (%) *
                        </label>
                        <input
                            type="number"
                            value={strategyData.objectives.maxDrawdown}
                            onChange={(e) => setStrategyData(prev => ({
                                ...prev,
                                objectives: { ...prev.objectives, maxDrawdown: e.target.value }
                            }))}
                            placeholder="20"
                            min="0"
                            max="50"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">How much can portfolio drop before rebalancing? (10-20% typical)</p>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                        <label className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={strategyData.objectives.capitalPreservation}
                                onChange={(e) => setStrategyData(prev => ({
                                    ...prev,
                                    objectives: { ...prev.objectives, capitalPreservation: e.target.checked }
                                }))}
                                className="w-4 h-4"
                            />
                            <span className="text-sm text-gray-700">Capital Preservation (maintain real value)</span>
                        </label>
                        
                        <label className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={strategyData.objectives.incomeGeneration}
                                onChange={(e) => setStrategyData(prev => ({
                                    ...prev,
                                    objectives: { ...prev.objectives, incomeGeneration: e.target.checked }
                                }))}
                                className="w-4 h-4"
                            />
                            <span className="text-sm text-gray-700">Income Generation (dividends/rent)</span>
                        </label>
                    </div>
                    
                    {strategyData.objectives.incomeGeneration && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Target Annual Income
                            </label>
                            <input
                                type="number"
                                value={strategyData.objectives.targetIncome}
                                onChange={(e) => setStrategyData(prev => ({
                                    ...prev,
                                    objectives: { ...prev.objectives, targetIncome: e.target.value }
                                }))}
                                placeholder="50000"
                                min="0"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    )}
                </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-2">✅ Example Compliant Statement</h4>
                <div className="bg-white p-3 rounded border border-green-200 text-sm text-gray-700">
                    <p className="italic">
                        "The fund aims to achieve real returns (after inflation) of CPI + 3% per annum over rolling 10-year periods,
                        with capital preservation during retirement phase and a maximum drawdown tolerance of 20%. This balanced approach
                        is appropriate for members aged {strategyData.members[0]?.age || 'XX'} approaching retirement."
                    </p>
                </div>
            </div>
        </div>
    );
    
    const renderAllocationStep = () => (
        <div className="space-y-6">
            <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-purple-800 mb-2">📊 Asset Allocation & Diversification (Mandatory Element #2)</h3>
                <p className="text-sm text-purple-700">
                    Define target allocation ranges for each asset class. If not diversified (e.g., 100% in one property),
                    you must provide justification.
                </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-4">Set Target Allocation Ranges</h4>
                <p className="text-sm text-gray-600 mb-4">
                    For each asset class, set minimum and maximum percentages. Total maximum should be ~100%.
                </p>
                
                <div className="space-y-4">
                    {['ausShares', 'intlShares', 'property', 'bonds', 'cash'].map(asset => {
                        const labels = {
                            ausShares: '🇦🇺 Australian Shares',
                            intlShares: '🌎 International Shares',
                            property: '🏢 Direct Property',
                            bonds: '📜 Fixed Income / Bonds',
                            cash: '💰 Cash / Term Deposits'
                        };
                        
                        return (
                            <div key={asset} className="p-4 bg-gray-50 rounded-lg">
                                <div className="font-medium text-gray-800 mb-3">{labels[asset]}</div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">
                                            Current % 
                                            <span className="ml-1 text-xs text-blue-600" title="Auto-calculated from Asset Valuation Manager">🔒</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={strategyData.assetAllocation[asset].current}
                                            readOnly
                                            disabled
                                            placeholder="0"
                                            min="0"
                                            max="100"
                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-gray-100 cursor-not-allowed"
                                            title="This value is automatically calculated from the Asset Valuation Manager tab"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">
                                            Min %
                                            {asset === 'property' && <span className="ml-1 text-xs text-blue-600" title="Managed in Asset Valuation Manager">🔒</span>}
                                        </label>
                                        <input
                                            type="number"
                                            value={strategyData.assetAllocation[asset].targetMin}
                                            onChange={(e) => setStrategyData(prev => ({
                                                ...prev,
                                                assetAllocation: {
                                                    ...prev.assetAllocation,
                                                    [asset]: { ...prev.assetAllocation[asset], targetMin: e.target.value }
                                                }
                                            }))}
                                            placeholder="0"
                                            min="0"
                                            max="100"
                                            readOnly={asset === 'property'}
                                            disabled={asset === 'property'}
                                            className={`w-full px-2 py-1 border border-gray-300 rounded text-sm ${asset === 'property' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                            title={asset === 'property' ? 'Property targets are managed in Asset Valuation Manager' : ''}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">
                                            Max %
                                            {asset === 'property' && <span className="ml-1 text-xs text-blue-600" title="Managed in Asset Valuation Manager">🔒</span>}
                                        </label>
                                        <input
                                            type="number"
                                            value={strategyData.assetAllocation[asset].targetMax}
                                            onChange={(e) => setStrategyData(prev => ({
                                                ...prev,
                                                assetAllocation: {
                                                    ...prev.assetAllocation,
                                                    [asset]: { ...prev.assetAllocation[asset], targetMax: e.target.value }
                                                }
                                            }))}
                                            placeholder="0"
                                            min="0"
                                            max="100"
                                            readOnly={asset === 'property'}
                                            disabled={asset === 'property'}
                                            className={`w-full px-2 py-1 border border-gray-300 rounded text-sm ${asset === 'property' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                            title={asset === 'property' ? 'Property targets are managed in Asset Valuation Manager' : ''}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 rounded">
                    <div className="text-sm text-blue-700">
                        <strong>Current Total:</strong> {
                            Object.values(strategyData.assetAllocation)
                                .reduce((sum, asset) => sum + (Number(asset.current) || 0), 0)
                        }%
                        {' | '}
                        <strong>Target Max Total:</strong> {
                            Object.values(strategyData.assetAllocation)
                                .reduce((sum, asset) => sum + (Number(asset.targetMax) || 0), 0)
                        }%
                    </div>
                </div>
            </div>
            
            {/* Property Details from Asset Valuation Manager */}
            {smsfState && (smsfState.residentialPropertyValue > 0 || smsfState.commercialPropertyValue > 0) && (
                <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                    <h4 className="font-semibold text-green-800 mb-3">🏢 Property Portfolio Details</h4>
                    <p className="text-sm text-green-700 mb-4">Auto-populated from Asset Valuation Manager</p>
                    
                    <div className="grid grid-cols-2 gap-4">
                        {smsfState.residentialPropertyValue > 0 && (
                            <div className="bg-white p-4 rounded-lg">
                                <h5 className="font-semibold text-gray-800 mb-2">🏘️ Residential Property</h5>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Value:</span>
                                        <span className="font-semibold">${(smsfState.residentialPropertyValue || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Annual Rental Income:</span>
                                        <span className="font-semibold text-green-600">${(smsfState.residentialRentalIncome || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Rental Yield:</span>
                                        <span className="font-semibold">
                                            {smsfState.residentialPropertyValue > 0 
                                                ? ((smsfState.residentialRentalIncome / smsfState.residentialPropertyValue) * 100).toFixed(2)
                                                : '0.00'}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {smsfState.commercialPropertyValue > 0 && (
                            <div className="bg-white p-4 rounded-lg">
                                <h5 className="font-semibold text-gray-800 mb-2">🏢 Commercial Property</h5>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Value:</span>
                                        <span className="font-semibold">${(smsfState.commercialPropertyValue || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Annual Rental Income:</span>
                                        <span className="font-semibold text-green-600">${(smsfState.commercialRentalIncome || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Rental Yield:</span>
                                        <span className="font-semibold">
                                            {smsfState.commercialPropertyValue > 0 
                                                ? ((smsfState.commercialRentalIncome / smsfState.commercialPropertyValue) * 100).toFixed(2)
                                                : '0.00'}%
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="mt-3 p-3 bg-white rounded border border-green-200">
                        <div className="flex justify-between text-sm font-semibold">
                            <span className="text-gray-700">Total Property Value:</span>
                            <span className="text-gray-900">${((smsfState.residentialPropertyValue || 0) + (smsfState.commercialPropertyValue || 0)).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm font-semibold mt-1">
                            <span className="text-gray-700">Total Annual Rental Income:</span>
                            <span className="text-green-600">${((smsfState.residentialRentalIncome || 0) + (smsfState.commercialRentalIncome || 0)).toLocaleString()}</span>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-semibold text-yellow-800 mb-2">📋 Rebalancing Strategy</h4>
                <div className="grid grid-cols-3 gap-4 mt-3">
                    <div>
                        <label className="block text-xs text-gray-700 mb-1">Rebalancing Trigger</label>
                        <select
                            value={strategyData.rebalancing.trigger}
                            onChange={(e) => setStrategyData(prev => ({
                                ...prev,
                                rebalancing: { ...prev.rebalancing, trigger: e.target.value }
                            }))}
                            className="w-full px-2 py-2 border border-gray-300 rounded text-sm"
                        >
                            <option value="3">3% deviation</option>
                            <option value="5">5% deviation</option>
                            <option value="10">10% deviation</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-gray-700 mb-1">Review Frequency</label>
                        <select
                            value={strategyData.rebalancing.frequency}
                            onChange={(e) => setStrategyData(prev => ({
                                ...prev,
                                rebalancing: { ...prev.rebalancing, frequency: e.target.value }
                            }))}
                            className="w-full px-2 py-2 border border-gray-300 rounded text-sm"
                        >
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                            <option value="annually">Annually</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-gray-700 mb-1">Method</label>
                        <select
                            value={strategyData.rebalancing.method}
                            onChange={(e) => setStrategyData(prev => ({
                                ...prev,
                                rebalancing: { ...prev.rebalancing, method: e.target.value }
                            }))}
                            className="w-full px-2 py-2 border border-gray-300 rounded text-sm"
                        >
                            <option value="tax-efficient">Tax-efficient</option>
                            <option value="immediate">Immediate</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
    
    const renderLiquidityStep = () => (
        <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-2">💧 Liquidity Assessment (Mandatory Elements #3 & #4)</h3>
                <p className="text-sm text-blue-700">
                    Calculate specific dollar amounts for pension payments and expenses. Show how you'll meet these obligations.
                </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-4">Annual Cash Flow Requirements</h4>
                
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Pension Payments
                        </label>
                        {strategyData.members.map((member, idx) => {
                            const minRate = member.age >= 65 ? 0.05 : 0.04;
                            const minPension = member.balance * minRate;
                            
                            return (
                                <div key={idx} className="mb-2 p-3 bg-gray-50 rounded">
                                    <div className="text-sm font-medium text-gray-700 mb-1">
                                        {member.name || `Member ${idx + 1}`} (Age {member.age})
                                    </div>
                                    <div className="text-xs text-gray-600 mb-2">
                                        Minimum required: ${Math.round(minPension).toLocaleString()}/year ({(minRate * 100).toFixed(0)}% of ${Number(member.balance).toLocaleString()})
                                    </div>
                                    <input
                                        type="number"
                                        placeholder={Math.round(minPension)}
                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                </div>
                            );
                        })}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">Admin Fees ($/year)</label>
                            <input
                                type="number"
                                value={strategyData.liquidity.operatingExpenses.adminFees}
                                onChange={(e) => setStrategyData(prev => ({
                                    ...prev,
                                    liquidity: {
                                        ...prev.liquidity,
                                        operatingExpenses: { ...prev.liquidity.operatingExpenses, adminFees: e.target.value }
                                    }
                                }))}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">Audit Fees ($/year)</label>
                            <input
                                type="number"
                                value={strategyData.liquidity.operatingExpenses.auditFees}
                                onChange={(e) => setStrategyData(prev => ({
                                    ...prev,
                                    liquidity: {
                                        ...prev.liquidity,
                                        operatingExpenses: { ...prev.liquidity.operatingExpenses, auditFees: e.target.value }
                                    }
                                }))}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">Accounting Fees ($/year)</label>
                            <input
                                type="number"
                                value={strategyData.liquidity.operatingExpenses.accountingFees}
                                onChange={(e) => setStrategyData(prev => ({
                                    ...prev,
                                    liquidity: {
                                        ...prev.liquidity,
                                        operatingExpenses: { ...prev.liquidity.operatingExpenses, accountingFees: e.target.value }
                                    }
                                }))}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">Insurance Premiums ($/year)</label>
                            <input
                                type="number"
                                value={strategyData.liquidity.operatingExpenses.insurancePremiums}
                                onChange={(e) => setStrategyData(prev => ({
                                    ...prev,
                                    liquidity: {
                                        ...prev.liquidity,
                                        operatingExpenses: { ...prev.liquidity.operatingExpenses, insurancePremiums: e.target.value }
                                    }
                                }))}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                        </div>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Minimum Cash Reserve ($) *
                        </label>
                        <input
                            type="number"
                            value={strategyData.liquidity.minimumCash}
                            onChange={(e) => setStrategyData(prev => ({
                                ...prev,
                                liquidity: { ...prev.liquidity, minimumCash: e.target.value }
                            }))}
                            placeholder="30000"
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Recommendation: 6-12 months of expenses</p>
                    </div>
                </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-2">📊 Liquidity Summary (Auto-calculated)</h4>
                <div className="text-sm text-green-700 space-y-2">
                    {(() => {
                        const totalExpenses = 
                            (Number(strategyData.liquidity.operatingExpenses.adminFees) || 0) +
                            (Number(strategyData.liquidity.operatingExpenses.auditFees) || 0) +
                            (Number(strategyData.liquidity.operatingExpenses.accountingFees) || 0) +
                            (Number(strategyData.liquidity.operatingExpenses.insurancePremiums) || 0);
                        
                        const totalPensions = strategyData.members.reduce((sum, m) => {
                            const minRate = m.age >= 65 ? 0.05 : 0.04;
                            return sum + (m.balance * minRate);
                        }, 0);
                        
                        const totalAnnualNeeds = totalExpenses + totalPensions;
                        const monthsOfReserve = totalAnnualNeeds > 0 
                            ? (Number(strategyData.liquidity.minimumCash) || 0) / (totalAnnualNeeds / 12)
                            : 0;
                        
                        return (
                            <>
                                <div><strong>Total Annual Pension Payments:</strong> ${Math.round(totalPensions).toLocaleString()}</div>
                                <div><strong>Total Operating Expenses:</strong> ${Math.round(totalExpenses).toLocaleString()}</div>
                                <div><strong>Total Annual Needs:</strong> ${Math.round(totalAnnualNeeds).toLocaleString()}</div>
                                <div><strong>Cash Reserve:</strong> ${Number(strategyData.liquidity.minimumCash || 0).toLocaleString()}</div>
                                <div className={monthsOfReserve >= 6 ? 'text-green-800 font-semibold' : 'text-red-600 font-semibold'}>
                                    <strong>Months of Reserve:</strong> {monthsOfReserve.toFixed(1)} months {monthsOfReserve >= 6 ? '✅' : '⚠️ Consider increasing'}
                                </div>
                            </>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
    
    const renderInsuranceStep = () => (
        <div className="space-y-6">
            <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="font-semibold text-orange-800 mb-2">🏥 Insurance Consideration (Mandatory Element #5)</h3>
                <p className="text-sm text-orange-700">
                    You MUST assess insurance needs for each member, even if you decide not to hold insurance.
                    Document your decision and reasoning.
                </p>
            </div>
            
            <div className="space-y-4">
                {strategyData.members.map((member, idx) => (
                    <div key={idx} className="bg-white p-6 rounded-lg border-2 border-gray-200">
                        <h4 className="font-semibold text-gray-800 mb-4">
                            {member.name || `Member ${idx + 1}`} (Age {member.age})
                        </h4>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Life Insurance
                                </label>
                                <div className="flex items-center space-x-4 mb-2">
                                    <label className="flex items-center">
                                        <input type="radio" name={`life-${idx}`} value="held" className="mr-2" />
                                        <span className="text-sm">Held within SMSF</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="radio" name={`life-${idx}`} value="external" className="mr-2" />
                                        <span className="text-sm">Held externally</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="radio" name={`life-${idx}`} value="not-required" className="mr-2" defaultChecked />
                                        <span className="text-sm">Not required</span>
                                    </label>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Justification (required)"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Example: "Not required - age {member.age}, no dependents" or "Held - 2 children, mortgage $200k"
                                </p>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    TPD (Total & Permanent Disability)
                                </label>
                                <div className="flex items-center space-x-4 mb-2">
                                    <label className="flex items-center">
                                        <input type="radio" name={`tpd-${idx}`} value="held" className="mr-2" />
                                        <span className="text-sm">Held within SMSF</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="radio" name={`tpd-${idx}`} value="external" className="mr-2" />
                                        <span className="text-sm">Held externally</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="radio" name={`tpd-${idx}`} value="not-required" className="mr-2" defaultChecked />
                                        <span className="text-sm">Not required</span>
                                    </label>
                                </div>
                                <input
                                    type="text"
                                    placeholder="Justification (required)"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold text-purple-800 mb-2">💡 Insurance Best Practices</h4>
                <ul className="list-disc list-inside text-purple-700 text-sm space-y-1 ml-4">
                    <li><strong>Age 60+:</strong> Usually not required (no employment income to protect)</li>
                    <li><strong>With dependents:</strong> Consider life insurance (cover until children independent)</li>
                    <li><strong>Working age:</strong> TPD may be appropriate if sole income earner</li>
                    <li><strong>High balance ($1M+):</strong> Self-insurance may be more cost-effective</li>
                </ul>
            </div>
        </div>
    );
    
    const renderReviewStep = () => {
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
        const suggestedDate = oneYearFromNow.toISOString().split('T')[0];
        
        return (
            <div className="space-y-6">
                <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-purple-800 mb-2">📅 Review Schedule</h3>
                    <p className="text-sm text-purple-700">
                        The ATO requires that investment strategies be reviewed regularly and updated when circumstances change.
                    </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Next Review Date *
                        </label>
                        <input
                            type="date"
                            value={strategyData.review.nextReviewDate}
                            onChange={(e) => setStrategyData(prev => ({
                                ...prev,
                                review: { ...prev.review, nextReviewDate: e.target.value }
                            }))}
                            min={suggestedDate}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">Recommended: {suggestedDate} (1 year from today)</p>
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Review Triggers (select all that apply)
                        </label>
                        <div className="space-y-2">
                            {[
                                'Member retires',
                                'Member employment status changes',
                                'Member has major life event (marriage, divorce, birth)',
                                'Fund balance changes by more than 20%',
                                'Investment performance falls below target for 2 consecutive years',
                                'Asset allocation breaches target range for more than 6 months',
                                'Legislation or tax law changes',
                                'New investment opportunity arises',
                                'Annually (mandatory)'
                            ].map((trigger, tidx) => (
                                <label key={tidx} className="flex items-start space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={strategyData.review.reviewTriggers.includes(trigger)}
                                        onChange={(e) => {
                                            const newTriggers = e.target.checked
                                                ? [...strategyData.review.reviewTriggers, trigger]
                                                : strategyData.review.reviewTriggers.filter(t => t !== trigger);
                                            setStrategyData(prev => ({
                                                ...prev,
                                                review: { ...prev.review, reviewTriggers: newTriggers }
                                            }));
                                        }}
                                        className="mt-1 w-4 h-4"
                                    />
                                    <span className="text-sm text-gray-700">{trigger}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    const renderSummaryStep = () => (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
                <h3 className="font-bold text-gray-800 text-2xl mb-2">📋 Compliance Check</h3>
                <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-700 font-medium">Compliance Score:</span>
                        <span className={`text-2xl font-bold ${
                            complianceScore === 100 ? 'text-green-600' :
                            complianceScore >= 80 ? 'text-yellow-600' :
                            'text-red-600'
                        }`}>
                            {complianceScore}%
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                            className={`h-3 rounded-full transition-all ${
                                complianceScore === 100 ? 'bg-green-600' :
                                complianceScore >= 80 ? 'bg-yellow-600' :
                                'bg-red-600'
                            }`}
                            style={{ width: `${complianceScore}%` }}
                        />
                    </div>
                </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
                {[
                    { label: 'Member Circumstances', check: strategyData.members.length > 0 && strategyData.members[0].name },
                    { label: 'Investment Objectives', check: strategyData.objectives.targetReturn },
                    { label: 'Asset Allocation', check: Object.values(strategyData.assetAllocation).some(a => a.targetMax > 0) },
                    { label: 'Liquidity Assessed', check: strategyData.liquidity.minimumCash },
                    { label: 'Insurance Considered', check: true }, // Always true since we have the step
                    { label: 'Review Scheduled', check: strategyData.review.nextReviewDate }
                ].map((item, idx) => (
                    <div key={idx} className={`p-4 rounded-lg ${item.check ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="flex items-center justify-between">
                            <span className={`text-sm font-medium ${item.check ? 'text-green-800' : 'text-red-800'}`}>
                                {item.label}
                            </span>
                            <span className="text-2xl">{item.check ? '✅' : '❌'}</span>
                        </div>
                    </div>
                ))}
            </div>
            
            {complianceScore === 100 && (
                <div className="bg-green-50 p-6 rounded-lg border-2 border-green-500">
                    <h4 className="font-bold text-green-800 text-lg mb-2">🎉 Strategy is ATO Compliant!</h4>
                    <p className="text-green-700 mb-4">
                        All 6 mandatory elements have been addressed. Your investment strategy is ready to generate.
                    </p>
                    <button
                        onClick={generatePDF}
                        className="w-full py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition font-semibold text-lg"
                    >
                        📄 Generate Investment Strategy PDF
                    </button>
                    <p className="text-xs text-green-600 text-center mt-2">
                        Includes: Strategy document + Trustee minutes template + Review checklist
                    </p>
                </div>
            )}
            
            {complianceScore < 100 && (
                <div className="bg-yellow-50 p-6 rounded-lg border-2 border-yellow-500">
                    <h4 className="font-bold text-yellow-800 text-lg mb-2">⚠️ Incomplete Strategy</h4>
                    <p className="text-yellow-700 mb-3">
                        Complete all mandatory elements before generating your strategy document.
                    </p>
                    <button
                        onClick={() => {
                            // Find first incomplete step
                            const checks = [
                                strategyData.members.length > 0 && strategyData.members[0].name,
                                strategyData.objectives.targetReturn,
                                Object.values(strategyData.assetAllocation).some(a => a.targetMax > 0),
                                strategyData.liquidity.minimumCash,
                                true,
                                strategyData.review.nextReviewDate
                            ];
                            const firstIncomplete = checks.findIndex(c => !c);
                            setCurrentStep(firstIncomplete + 1);
                        }}
                        className="w-full py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition"
                    >
                        Go to First Incomplete Section
                    </button>
                </div>
            )}
        </div>
    );
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
                    <h2 className="text-2xl font-bold mb-2">SMSF Investment Strategy Builder</h2>
                    <p className="text-blue-100 text-sm">Generate ATO-compliant investment strategy in 7 steps</p>
                </div>
                
                {/* Progress Bar */}
                <div className="px-6 py-4 bg-gray-50 border-b">
                    <div className="flex justify-between items-center mb-2">
                        {wizardSteps.map((step, idx) => (
                            <div
                                key={idx}
                                className={`flex-1 ${idx !== 0 ? 'ml-2' : ''}`}
                            >
                                <div className={`h-2 rounded-full transition-all ${
                                    idx <= currentStep ? 'bg-blue-600' : 'bg-gray-300'
                                }`} />
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-between text-xs text-gray-600">
                        <span>Step {currentStep + 1} of {wizardSteps.length}</span>
                        <span>Compliance: {complianceScore}%</span>
                    </div>
                </div>
                
                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-1">
                            {wizardSteps[currentStep].title}
                        </h3>
                        <p className="text-sm text-gray-600">
                            {wizardSteps[currentStep].description}
                        </p>
                    </div>
                    
                    {validationErrors.length > 0 && (
                        <div className="mb-4 p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                            <p className="font-semibold text-red-800 mb-2">Please fix the following:</p>
                            <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                                {validationErrors.map((error, idx) => (
                                    <li key={idx}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                    {renderStepContent()}
                </div>
                
                {/* Footer Navigation */}
                <div className="border-t bg-gray-50 p-6">
                    <div className="flex justify-between">
                        <button
                            onClick={onCancel}
                            className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition"
                        >
                            Cancel
                        </button>
                        
                        <div className="flex space-x-3">
                            {currentStep > 0 && (
                                <button
                                    onClick={handlePrevious}
                                    className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                                >
                                    ← Previous
                                </button>
                            )}
                            
                            {currentStep < wizardSteps.length - 1 && (
                                <button
                                    onClick={handleNext}
                                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-semibold"
                                >
                                    Next →
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvestmentStrategyBuilder;

